import groupBy from 'lodash/groupBy';
import pThrottle from 'p-throttle';
import request from '@apps-common/js-client/api/request';
import {PDA_ADMIN_URL} from '@app/config';
import {queryKeys as estimatesQueryKeys} from './quotaEstimates.js';
import {getHashFromId} from '@app/util/granero';
import {queryKeys as productsQueryKeys} from './products.js';
import {removeEmpty} from './util.js';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

const MAX_RESERVATION_PAYLOAD_BYTES = 9000000; // ~9MB

const throttle = pThrottle({
  limit: 4,
  interval: 1000,
});

export const queryKeys = {
  all: ['quotaReservations'],
  lists: () => [...queryKeys.all, 'list'],
  list: filters => [...queryKeys.lists(), removeEmpty({...filters})],
  details: () => [...queryKeys.all, 'detail'],
  detail: id => [...queryKeys.details(), id],
};

const getQuotaReservations = params => {
  if (params?.collection_id) {
    params.collection_id = getHashFromId(params.collection_id);
  }
  const config = {
    params: {
      ...params,
      limit: params.limit || 250,
    },
  };
  return new Promise((resolve, reject) => {
    let all = [];

    const handler = async response => {
      all = [...all, ...(response?.data?.results ?? [])];

      const nextLink = response?.data?.meta?.next;

      if (nextLink) {
        request.get(nextLink).then(handler).catch(reject);
        await setTimeout(100);
      } else {
        resolve({reservations: all, count: response?.data?.meta?.count});
      }
    };

    request
      .get(`${PDA_ADMIN_URL}/quota-reservations/`, config)
      .then(handler)
      .catch(reject);
  });
};

const postQuotaReservationRequest = data =>
  request
    .post(`${PDA_ADMIN_URL}/quota-reservations/bulk-reserve`, data)
    .then(res => res.data);

const postBatchedReservations = async ({aoi_refs, product_access_id}) => {
  const results = [];

  // Chunk features into chunks of ~9MB as we need the payload to be < 10MB
  const {chunkedFeatures} = aoi_refs.reduce(
    (accumulator, feature) => {
      if (accumulator.chunkedFeatures.length !== accumulator.index + 1) {
        accumulator.chunkedFeatures.push([]);
      }

      const bytes = Buffer.byteLength(
        JSON.stringify(accumulator.chunkedFeatures[accumulator.index])
      );

      if (bytes <= MAX_RESERVATION_PAYLOAD_BYTES) {
        accumulator.chunkedFeatures[accumulator.index].push(feature);
      } else {
        accumulator.index++;
        accumulator.chunkedFeatures.push([feature]);
      }

      return accumulator;
    },
    {
      chunkedFeatures: [],
      index: 0,
    }
  );

  for (const refsChunk of chunkedFeatures) {
    const result = await postQuotaReservationRequest({
      aoi_refs: refsChunk,
      product_access_id,
    }).catch(error => {
      return {
        aoi_refs: refsChunk,
        product_access_id,
        error,
      };
    });

    results.push(result);
  }

  return results;
};

const deleteReservationRequest = id =>
  request
    .delete(`${PDA_ADMIN_URL}/quota-reservations/${id}`)
    .then(res => res.data);

const throttledDelete = throttle(async id => {
  return await deleteReservationRequest(id);
});

const getReservedProductsForCollection = params => {
  if (!params || !params.collection_id) {
    throw 'Collection id is required';
  }

  const config = {
    params: {
      ...params,
      collection_id: getHashFromId(params.collection_id),
    },
  };
  return request
    .get(`${PDA_ADMIN_URL}/quota-reservations/products/`, config)
    .then(res => res.data);
};

export const useGetReservations = ({...params} = {}, queryOptions = {}) => {
  return useQuery({
    queryKey: queryKeys.list(params),
    queryFn: () => getQuotaReservations(params),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
};

export const useGetReservationsByProduct = (
  {...params} = {},
  queryOptions = {}
) => {
  return useGetReservations(params, {
    ...queryOptions,
    select: data => {
      const groupedReservations = groupBy(data.reservations, 'product_id');
      return {
        reservations: groupedReservations,
        productCount: Object.keys(groupedReservations).length,
        list: data.reservations,
      };
    },
  });
};

export const useGetReservedProductsForCollection = (
  collectionId,
  queryOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.list({products: true, collection_id: collectionId}),
    queryFn: throttle(async () => {
      return await getReservedProductsForCollection({
        collection_id: collectionId,
      });
    }),
    select: data => data.products,
    ...queryOptions,
  });
};

export const useMakeQuotaReservations = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({productId, refs}) =>
      postBatchedReservations({
        product_access_id: productId,
        aoi_refs: refs,
      }),
    ...queryOptions,
    onSuccess: (data, args) => {
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
    onSettled: (data, error, args) => {
      queryClient.invalidateQueries({
        queryKey: estimatesQueryKeys.list({
          product_access_id: args.productId,
        }),
        refetchType: 'none',
      });

      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      });

      if (args.collectionId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.list({collection_id: args.collectionId}),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: queryKeys.lists(),
        });
      }
    },
  });
};

export const useDeleteQuotaReservation = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({id}) => throttledDelete(id),
    ...queryOptions,
    onSuccess: (data, args) => {
      const previousData = queryClient.getQueryData(
        queryKeys.list({collection_id: args.collectionId})
      );

      const newReservations = previousData?.reservations.filter(
        r => r.id !== args.id
      );

      queryClient.setQueryData(
        queryKeys.list({collection_id: args.collectionId}),
        {reservations: newReservations}
      );

      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
  });
};
